import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Title, Text, Group, Skeleton, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';

const icon = (children: ReactNode) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    {children}
  </svg>
);

/*
 * The four things a platform administrator can be told about, with the stored
 * preference key beside each. The key is data and is never translated; the
 * label and the sentence are catalogue keys resolved at render, because this
 * table is module scope and cannot call the hook.
 *
 * The reference design draws a second column of in-app switches beside these.
 * Nothing in the platform dispatches an in-app notification from a preference,
 * so that column would be eight stored booleans instead of four with no more
 * meaning, and it is left out until there is a dispatcher to honour it.
 */
const CHANNELS: { key: string; labelKey: string; hintKey: string; icon: ReactNode }[] = [
  {
    key: 'admin_security',
    labelKey: 'settings.profile.notifications.security.label',
    hintKey: 'settings.profile.notifications.security.hint',
    icon: icon(<><rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="M3.5 7l8.5 6 8.5-6" /></>)
  },
  {
    key: 'admin_platform_updates',
    labelKey: 'settings.profile.notifications.platform.label',
    hintKey: 'settings.profile.notifications.platform.hint',
    icon: icon(<><path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6z" /><path d="M10 19a2 2 0 0 0 4 0" /></>)
  },
  {
    key: 'admin_audit',
    labelKey: 'settings.profile.notifications.audit.label',
    hintKey: 'settings.profile.notifications.audit.hint',
    icon: icon(<><path d="M5 20V10M12 20V4M19 20v-7" /></>)
  },
  {
    key: 'admin_billing',
    labelKey: 'settings.profile.notifications.billing.label',
    hintKey: 'settings.profile.notifications.billing.hint',
    icon: icon(<><rect x="3.5" y="5" width="17" height="14" rx="2" /><path d="M3.5 10h17" /><path d="M7 14.5h4" /></>)
  }
];

/**
 * What the platform emails this administrator about.
 *
 * Each switch writes on its own rather than queueing behind a Save button: four
 * independent booleans behind one commit is the pattern people most often
 * forget to press, and the write is a single PUT of the whole bag anyway. A
 * failed write puts the switch back where it was and says so.
 *
 * Nothing is interactive until the stored values are in hand. The preference
 * endpoint replaces the whole dictionary, so toggling one switch against an
 * assumed-empty map would write four `false`s over four `true`s.
 */
export function NotificationPrefsPanel() {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin', 'notification-prefs'],
    queryFn: () => identity.getNotificationPrefs()
  });

  /*
   * A stored preference is `boolean | Record<string, boolean>` since the
   * customer console started letting a tenant pick channels per notification.
   * These four are the administrator's own plain on/off toggles and are never
   * written as a channel map, so `=== true` still reads them correctly; the
   * type is widened only so the shared SDK shape fits.
   */
  const [prefs, setPrefs] = useState<identity.NotificationPrefs>({});

  useEffect(() => {
    if (data?.data) setPrefs(data.data);
  }, [data]);

  const save = useMutation({
    mutationFn: (next: identity.NotificationPrefs) => identity.updateNotificationPrefs(next),
    onMutate: (next) => {
      const previous = prefs;
      setPrefs(next);
      return { previous };
    },
    onError: (e, _next, ctx) => {
      if (ctx?.previous) setPrefs(ctx.previous);
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.profile.notifications.saveFailed'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'notification-prefs'] })
  });

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={5}>{t('settings.profile.notifications.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.profile.notifications.subtitle')}</Text>
          {user?.email && (
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('settings.profile.notifications.sentTo', { email: user.email })}
            </Text>
          )}
        </Stack>

        {error && (
          <Alert color="error" role="alert">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('settings.profile.notifications.loadError')}</Text>
              <Button size="xs" variant="outline" onClick={() => { void refetch(); }}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}

        {isPending && !error && (
          <Stack gap="sm">{CHANNELS.map((c) => <Skeleton key={c.key} height={44} />)}</Stack>
        )}

        {!isPending && !error && (
          <div className="rr-profile-prefs">
            <div className="rr-profile-prefs__head">
              <Text role="overline" c="var(--rr-color-text-muted)">{t('settings.profile.notifications.columns.channel')}</Text>
              <Text role="overline" c="var(--rr-color-text-muted)">{t('settings.profile.notifications.columns.when')}</Text>
              <Text role="overline" c="var(--rr-color-text-muted)" className="rr-profile-prefs__switch">
                {t('settings.profile.notifications.columns.email')}
              </Text>
            </div>
            {CHANNELS.map((channel) => (
              <div className="rr-profile-prefs__row" key={channel.key}>
                <span className="rr-profile-prefs__channel">
                  <span className="rr-profile-prefs__icon">{channel.icon}</span>
                  <Text role="bodySmall" fw={600}>{t(channel.labelKey)}</Text>
                </span>
                <Text role="caption" c="var(--rr-color-text-muted)" className="rr-profile-prefs__when">
                  {t(channel.hintKey)}
                </Text>
                <span className="rr-profile-prefs__switch">
                  <Switch
                    checked={prefs[channel.key] === true}
                    aria-label={t(channel.labelKey)}
                    onChange={(checked) => save.mutate({ ...prefs, [channel.key]: checked })}
                  />
                </span>
              </div>
            ))}
          </div>
        )}

        {/* The registry is real and the write persists; the dispatcher is not
            built. Saying so here is cheaper than an operator waiting for an
            email that no service sends. */}
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('settings.profile.notifications.noDispatcher')}
        </Text>
      </Stack>
    </Card>
  );
}
